/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

/**
 * LicenseBanner
 * Shown at the top of AdminPanel when the license is in Warning or Soft Lock state.
 * Returns null in Normal state.
 */
const LicenseBanner = () => {
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLicense = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/license`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) setLicense(data.license);
    } catch {
      // Silently ignore network errors — banner is informational only
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicense();
    // Refresh every hour
    const interval = setInterval(fetchLicense, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLicense]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/license/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setLicense(data.license);
      }
    } catch {
      // Ignore
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || !license) return null;

  // Normal state — no banner
  if (!license.inWarning && !license.softLocked && !license.hardLocked) return null;

  const isHard = license.hardLocked;
  const isSoft = license.softLocked;
  // inWarning = valid but expiring soon

  const bgClass = isHard || isSoft
    ? 'bg-red-50 border-red-300 text-red-900'
    : 'bg-amber-50 border-amber-300 text-amber-900';

  const iconClass = isHard || isSoft ? 'text-red-500' : 'text-amber-500';

  const Icon = isHard || isSoft ? XCircle : AlertTriangle;

  let headline = '';
  let body = '';

  if (isSoft) {
    headline = `License Expired — ${license.daysExpired} day${license.daysExpired !== 1 ? 's' : ''} ago`;
    body = 'New student registrations and test creation are blocked until the license is renewed. Existing students and tests are unaffected.';
  } else if (isHard) {
    headline = 'License Invalid';
    body = 'The deployment license is invalid. The server may not be running correctly. Contact @baughtnet IT Solutions immediately.';
  } else {
    headline = `License Expires in ${license.daysUntilExpiry} day${license.daysUntilExpiry !== 1 ? 's' : ''}`;
    body = 'Please contact @baughtnet IT Solutions to renew before the license expires to avoid service interruption.';
  }

  return (
    <div className={`mx-0 mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 ${bgClass}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconClass}`} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{headline}</p>
        <p className="text-sm mt-0.5">{body}</p>
        {isSoft && (
          <p className="text-xs mt-1 font-medium">
            Blocked: new student registration, test creation, test cloning
          </p>
        )}
        {license.offlineMode && (
          <p className="text-xs mt-1 opacity-70">(offline mode — license server unreachable)</p>
        )}
      </div>
      <button
        onClick={handleManualRefresh}
        disabled={refreshing}
        title="Re-check license now"
        className="ml-2 shrink-0 rounded p-1 hover:bg-black/10 disabled:opacity-50 transition-colors"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default LicenseBanner;
